import { styled } from 'dripsy';
import React from 'react';
import { ModalComponentProp } from 'react-native-modalfy';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { IS_MOBILE, IS_WEB } from '@utils';
import { BaseText } from '../common/BaseText';
import { IconTouchable } from '../common/IconTouchable';
import { Separator } from '../common/Separator';
import { SubscribeSuggestionSvg } from '../svg/SubscribeSuggestionSvg';
import { Button } from '../common/Button';
import { ModalStackParamsType } from '../../modals/types';
import { ModalView } from './components/ModalView';
import { BottomModalView } from './components/BottomModalView';

const ModalViewContainer = IS_MOBILE ? BottomModalView : ModalView;

const CrossIconContainer = styled(View)({
  position: 'absolute',
  zIndex: 1,
  elevation: 1,
  top: IS_WEB ? '$9' : '$6',
  right: IS_WEB ? '$9' : '$8',
});

const Container = styled(View)({
  alignItems: 'center',
  pt: IS_WEB ? '$10' : '$9',
  px: IS_WEB ? '$8' : '$6',
  pb: IS_WEB ? '$8' : '$7',
});

const Title = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xlarge',
  fontWeight: 'semi',
  textAlign: 'center',
});

const Subtitle = styled(BaseText)({
  maxWidth: [null, 295],
  color: '$text400',
  variant: 'text.medium',
  textAlign: 'center',
});

export enum SubscribeSuggestionModalTypeEnum {
  Tutorial = 0,
  TrackFeedback = 1,
}

const typeLocalKeyMap: Record<
  SubscribeSuggestionModalTypeEnum,
  string
> = {
  [SubscribeSuggestionModalTypeEnum.Tutorial]: 'tutorial',
  [SubscribeSuggestionModalTypeEnum.TrackFeedback]: 'trackFeedback',
};

export interface ISubscribeSuggestionModalParams {
  onPress(): void;
  type?: SubscribeSuggestionModalTypeEnum;
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'SubscribeSuggestionModal'
>;

export function SubscribeSuggestionModal({ modal }: ModalPropsType) {
  const { t } = useTranslation();
  const { bottom } = useSafeAreaInsets();
  const type =
    modal.getParam('type') ??
    SubscribeSuggestionModalTypeEnum.Tutorial;

  function closeModalByCrossIcon() {
    modal.closeModal();
  }

  return (
    <ModalViewContainer bottomInset={bottom}>
      <CrossIconContainer>
        <IconTouchable
          onPress={closeModalByCrossIcon}
          name="close-light"
          size={24}
        />
      </CrossIconContainer>

      <Container>
        <SubscribeSuggestionSvg />

        <Separator />
        <Title>
          {`${t('common.subscribeSuggestion.title', {
            context: typeLocalKeyMap[type],
          })} 🤩`}
        </Title>

        <Separator size="$4" />
        <Subtitle>
          {t('common.subscribeSuggestion.subtitle', {
            context: typeLocalKeyMap[type],
          })}
        </Subtitle>

        <Separator />
        <Button onPress={modal.params?.onPress}>
          {t('common.subscribeSuggestion.select')}
        </Button>
      </Container>
    </ModalViewContainer>
  );
}
